import React, {
  useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Select,
  Input,
  Tag,
  Tooltip,
} from 'antd';
import { parse } from 'qs';
import moment from 'moment';
import {
  FunnelSimple,
  CaretDown,
  CaretUp,
  X,
} from '@phosphor-icons/react';
import DatePicker from '../../../../components/DatePicker';
import { getUserDepartmentsByQuery } from '../../../../actions/userProfile';
import { getLabelsByQuery } from '../../../../actions/labels';
import { getOrganizationTeams } from '../../../../actions/teams';
import { verifyFeatureAccess, hasFeatureInLocalStorage } from '../../../../global/featureFlags';
import { AWAITING_PROVIDER_FILTER_KEY, AWAITING_PROVIDER_LABEL } from '../../../../components/inbox/awaitingProvider';
import useAwaitingProviderEnabled from '../../../../components/inbox/useAwaitingProviderEnabled';
import {
  normalizeToArray,
} from '../../../../components/sidebar/SettingsComponents/utils/multiSelectUtils';
import {
  getCompactMultiSelectMaxTagCount,
  getCompactMultiSelectPlaceholder,
} from '../../../../utils/compactMultiSelect';
import { trackReferralFilterChanged } from '../../../../global/analytics';
import {
  AUTO_UPLOAD_FILTER_KEYS,
  normalizeAutoUploadFilterValues,
} from '../../../../components/chartActions/autoUploadFilterOptions';
import useAutoUploadFilterOptions from '../../../../components/chartActions/useAutoUploadFilterOptions';
import { captureAnalytics } from '../../../../actions/preload';
import { ReferralStatusLabel } from '../../shared/referralStatusPresentation';
import {
  REFERRAL_TEAM_VIEW_STORAGE_KEY,
  TEAM_VIEW_UNASSIGNED,
  normalizeTeamViewSelection,
  teamViewCategory,
  teamViewQueryValue,
  isSuccessfulTeamsFetch,
} from '../../shared/stickyTeamView';
import {
  toLocalDayStartInstant,
  toLocalDayEndInstant,
  parseDateFilterParam,
} from '../../shared/dateFilterParams';
import sortByNameAlphabetically from '../../../../utils/sortByNameAlphabetically';
import {
  fetchSavedViews, createSavedView, updateSavedView, deleteSavedView,
} from '../../../../actions/savedViews';
import { selectSavedViewsList } from '../../../../reducers/savedViews';
import SavedViewsBar from '../../../../components/savedViews/SavedViewsBar';
import SaveViewButton from '../../../../components/savedViews/SaveViewButton';
import {
  SAVED_VIEW_MARKER_KEY,
  buildSavedViewQueryUpdates,
  canClearSavedViewState,
  canonicalizeFilters,
  formatSavedViewSummary,
  pickSaveableParams,
} from '../../shared/savedViews/savedViewFilters';
import { useSavedViews, SAVED_VIEW_ANALYTICS_EVENT } from '../../shared/savedViews/useSavedViews';
import { useSavedViewOptions } from '../../shared/savedViews/useSavedViewOptions';

const parseOptions = { ignoreQueryPrefix: true };
const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY', 'YYYY-MM-DD'];

const teamViewChip = (team) => (
  <Tag style={{ backgroundColor: team.color, color: team.is_dark_text ? 'black' : 'white', margin: 0 }}>
    {team.name}
  </Tag>
);

// Received-date bounds ('received') are sent as exact UTC instants so the
// backend matches the user's local day instead of the UTC day; due and
// appointment ranges filter DATE columns and stay bare calendar dates.
const DATE_RANGE_CONFIGS = {
  received: {
    startKey: 'date_start', endKey: 'date_end', source: 'referred_date_range', asInstants: true,
  },
  due: {
    startKey: 'due_date_start', endKey: 'due_date_end', source: 'due_date_range',
  },
  appointment: {
    startKey: 'appointment_date_start', endKey: 'appointment_date_end', source: 'appointment_date_range',
  },
};
const NEW_REFERRAL_STATUS = 'new';
const SCHEDULED_REFERRAL_STATUS = 'scheduled';
const MOHS_SCHEDULED_REFERRAL_STATUS = 'mohs scheduled';

const REPLY_STATUS_OPTIONS = [
  { label: 'Success', value: 'success' },
  { label: 'Failed', value: 'failure' },
  { label: 'Queued', value: 'inprogress' },
  { label: 'Resent', value: 'resent' },
];

const SMS_STATUS_OPTIONS = [
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Failed', value: 'delivery_failed' },
  { label: 'Resent', value: 'resent' },
];

const AUTO_UPLOAD_KEYS = AUTO_UPLOAD_FILTER_KEYS;

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Oldest First', value: 'oldest' },
  { label: 'DOB (Youngest)', value: 'dob_youngest' },
  { label: 'DOB (Oldest)', value: 'dob_oldest' },
];

const ARCHIVED_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'All Records', value: 'both' },
];

// Keys whose presence in the URL auto-expands the secondary drawer. Params
// controlled exclusively from primary-row toggles (dob, scheduled, overdue,
// unread) are excluded so toggling them never reopens the drawer — they
// remain in ALL_FILTER_KEYS for active-filter counting.
const SECONDARY_FILTER_KEYS = [
  'tag', 'assignee',
  'date_start', 'date_end',
  'due_date_start', 'due_date_end', 'appointment_date_start',
  'appointment_date_end', 'reply_statuses', 'sms_statuses',
  'unassigned',
  ...AUTO_UPLOAD_KEYS,
];

const ALL_FILTER_KEYS = [
  'contains', 'referral_statuses', 'department',
  'dob',
  'scheduled', 'overdue', 'unread', AWAITING_PROVIDER_FILTER_KEY,
  ...SECONDARY_FILTER_KEYS,
];

const TRACKABLE_FILTER_KEYS = [
  ...ALL_FILTER_KEYS,
  'archived',
  'sort',
  'sort_dir',
];

const normalizeFilterValue = (value) => {
  if (value === undefined || value === null || value === '') return null;

  if (Array.isArray(value)) {
    const filteredValues = value
      .filter((item) => item !== undefined && item !== null && item !== '')
      .map((item) => String(item));
    if (filteredValues.length === 0) return null;
    return filteredValues.sort();
  }

  return String(value);
};

const areFilterValuesEqual = (previousValue, nextValue) => {
  if (previousValue === null && nextValue === null) return true;
  if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
    if (previousValue.length !== nextValue.length) return false;
    return previousValue.every((value, index) => value === nextValue[index]);
  }
  return previousValue === nextValue;
};

const getFilterValueCount = (value, key) => {
  if (value === null) return 0;
  if (Array.isArray(value)) return value.length;
  if (key === 'contains') return String(value).length;
  return 1;
};

const normalizeStatusValues = (values = []) => values
  .map((value) => String(value || '').trim().toLowerCase())
  .sort();

const SAVED_VIEWS_PAGE = 'referrals';

const readyOptions = (values) => ({ status: 'ready', values });

const areStatusSetsEqual = (left = [], right = []) => {
  const normalizedLeft = normalizeStatusValues(left);
  const normalizedRight = normalizeStatusValues(right);

  if (normalizedLeft.length !== normalizedRight.length) return false;
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const ReferralFilterBar = ({
  paramTools,
  getPageDataWithQuery,
  location,
  allReferredStatuses,
  departments,
  labels,
  departmentUsers,
  currentUserEmail,
  currentUserName,
  teams,
  getLabelsByQuery: fetchLabels,
  getUserDepartmentsByQuery: fetchDepartmentUsers,
  getOrganizationTeams: fetchTeams,
  autoUploadToEMR,
  createPatientChart,
  taskCreation,
  hideTaskButton,
  labelFilterRef,
  savedViewsEnabled,
  savedViewsControlsRef,
  savedViewsList,
  savedViewsUserId,
  savedViewsOrgId,
  fetchSavedViews: dispatchFetchSavedViews,
  createSavedView: dispatchCreateSavedView,
  updateSavedView: dispatchUpdateSavedView,
  deleteSavedView: dispatchDeleteSavedView,
}) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const awaitingProviderEnabled = useAwaitingProviderEnabled();
  const [searchValue, setSearchValue] = useState('');
  const [hasRequestedDepartmentUsers, setHasRequestedDepartmentUsers] = useState(false);
  const [teamsEnabled, setTeamsEnabled] = useState(() => hasFeatureInLocalStorage('teams') === true);
  const [teamsFetched, setTeamsFetched] = useState(false);
  const didApplyTeamStickyRef = useRef(false);
  const activeRequestController = useRef(null);

  const currentParams = useMemo(
    () => parse(location.search, parseOptions),
    [location.search],
  );

  // ENG-3551: the live URL's own params. Unlike paramTools.params they carry no
  // HOC-injected referrals/category, so a selection-only marker write changes
  // nothing in the URL but the marker.
  const liveParamsRef = useRef(currentParams);
  useLayoutEffect(() => {
    liveParamsRef.current = currentParams;
  }, [currentParams]);

  // ENG-3551: a URL that selects a saved view is that view's exact filters, so the
  // sticky team reconciliation below must not add or rewrite its team — decided at
  // mount, before the team list has loaded.
  useEffect(() => {
    if (savedViewsEnabled && liveParamsRef.current[SAVED_VIEW_MARKER_KEY]) {
      didApplyTeamStickyRef.current = true;
    }
  }, []);

  // Keep search input in sync with URL params
  useEffect(() => {
    if (currentParams.contains) setSearchValue(currentParams.contains);
    else if (currentParams.search) setSearchValue(currentParams.search);
    else setSearchValue('');
  }, [currentParams.contains, currentParams.search]);

  // Load filter data on mount
  useEffect(() => {
    fetchLabels('?sort=label_usage&sort_dir=DESC&page_size=max');
  }, []);

  // Teams (v3): converge the flag, then fetch the org teams ONCE for both the
  // team-view dropdown and the row pickers (shared via redux). Gated so flag-off
  // adds no dropdown and no fetch.
  useEffect(() => {
    let mounted = true;
    verifyFeatureAccess('teams').then((enabled) => {
      if (mounted) setTeamsEnabled(enabled === true);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Mark reconciliation-ready ONLY after a SUCCESSFUL team-list load. On a failed
  // or canceled fetch the Redux team list is empty/stale; treating it as
  // authoritative would strip a valid team from the URL and broaden Referrals, so
  // fail closed — leave teamsFetched false and preserve the URL/sticky selection.
  useEffect(() => {
    if (!teamsEnabled) return undefined;
    let mounted = true;
    Promise.resolve(fetchTeams())
      .then((result) => {
        if (mounted && isSuccessfulTeamsFetch(result)) setTeamsFetched(true);
      })
      .catch(() => { /* rejected fetch: fail closed, stay unreconciled */ });
    return () => { mounted = false; };
  }, [teamsEnabled, fetchTeams]);

  useEffect(() => () => {
    if (activeRequestController.current) {
      activeRequestController.current.abort();
    }
  }, []);

  // Auto-expand secondary filters only when the saved URL includes params
  // controlled by the secondary row. Primary-row params stay visible up top.
  useEffect(() => {
    const hasSecondary = SECONDARY_FILTER_KEYS.some((key) => {
      const val = currentParams[key];
      return val !== undefined && val !== '' && val !== null;
    });
    if (hasSecondary) {
      setShowMoreFilters(true);
    }
  }, [currentParams]);

  // Count active filters. The backend ignores awaitingProvider without
  // Awaiting Provider, so a stale URL param doesn't count.
  const activeFilterCount = useMemo(() => {
    let count = ALL_FILTER_KEYS.filter((key) => {
      if (key === AWAITING_PROVIDER_FILTER_KEY && !awaitingProviderEnabled) return false;
      const val = currentParams[key];
      return val !== undefined && val !== '' && val !== null
        && !(Array.isArray(val) && val.length === 0);
    }).length;
    if (currentParams.archived === 'true' || currentParams.archived === 'both') count += 1;
    return count;
  }, [currentParams, awaitingProviderEnabled]);

  const updateQuery = useCallback((updates, source = 'unknown') => {
    const {
      params, stringify, stringifyOptions, push,
    } = paramTools;
    const newParams = { ...params, ...updates, page: 1 };
    Object.keys(newParams).forEach((key) => {
      if (
        newParams[key] === null
        || newParams[key] === undefined
        || newParams[key] === ''
        || (Array.isArray(newParams[key]) && newParams[key].length === 0)
      ) {
        delete newParams[key];
      }
    });

    TRACKABLE_FILTER_KEYS.forEach((key) => {
      const previousValue = normalizeFilterValue(params[key]);
      const nextValue = normalizeFilterValue(newParams[key]);
      if (areFilterValuesEqual(previousValue, nextValue)) return;

      let operation = 'update';
      if (previousValue === null && nextValue !== null) operation = 'set';
      if (previousValue !== null && nextValue === null) operation = 'clear';

      trackReferralFilterChanged({
        filterKey: key,
        operation,
        source,
        valueCount: getFilterValueCount(nextValue, key),
        hasValue: nextValue !== null,
      });
    });

    const queryString = stringify(newParams, stringifyOptions);
    push(`${window.location.pathname}${queryString}`);
    if (activeRequestController.current) {
      activeRequestController.current.abort();
    }
    const nextRequestController = new AbortController();
    activeRequestController.current = nextRequestController;
    getPageDataWithQuery(queryString, nextRequestController.signal);
  }, [paramTools, getPageDataWithQuery]);

  // Teams (v3): sticky team-view resolution on mount (D6). Runs once the teams
  // flag + list converge. The records HOC already seeds the stored value into the
  // first fetch when the flag was cached, so here we mainly (a) reconcile the URL
  // the HOC set, and (b) re-drive the query when the flag was NOT cached at mount
  // and thus could not be seeded. A saved/seeded slug that no longer maps to a
  // live team (deleted) falls back to All and cleans the key.
  useEffect(() => {
    if (!teamsEnabled || !teamsFetched || didApplyTeamStickyRef.current) return;
    didApplyTeamStickyRef.current = true;

    let saved = null;
    try { saved = localStorage.getItem(REFERRAL_TEAM_VIEW_STORAGE_KEY); } catch { saved = null; }

    const isKnownTeam = (value) => (
      value === TEAM_VIEW_UNASSIGNED
      || (teams || []).some((team) => String(team.id) === String(value))
    );
    // Scalar for a single value (team=<slug>), array for several
    // (team[]=a&team[]=b) — the backend team filter accepts either.
    const asQueryValue = (values) => (values.length === 1 ? values[0] : values);
    const persistSticky = (values) => {
      try { localStorage.setItem(REFERRAL_TEAM_VIEW_STORAGE_KEY, values.join(',')); } catch { /* ignore */ }
    };
    const clearSticky = () => {
      try { localStorage.removeItem(REFERRAL_TEAM_VIEW_STORAGE_KEY); } catch { /* ignore */ }
    };

    const urlTeams = normalizeToArray(currentParams.team);
    if (urlTeams.length > 0) {
      // Teams already in the URL (deep link, or seeded from localStorage by the
      // records HOC before the first fetch) govern the query. Drop any slug that
      // no longer maps to a live team (deleted); if that empties the set, fall
      // back to All. Otherwise persist the surviving slugs as the sticky value.
      const knownTeams = urlTeams.filter(isKnownTeam);
      if (knownTeams.length !== urlTeams.length) {
        if (knownTeams.length === 0) {
          clearSticky();
          updateQuery({ team: null }, 'team_view_sticky');
          return;
        }
        persistSticky(knownTeams);
        updateQuery({ team: asQueryValue(knownTeams) }, 'team_view_sticky');
        return;
      }
      persistSticky(urlTeams);
      return;
    }

    // No team in the URL: apply the stored sticky value. This is the path when the
    // teams flag was not cached at mount (the HOC could not seed), so the flag/list
    // convergence re-drives the query here.
    const savedTeams = normalizeToArray(saved);
    if (savedTeams.length === 0) return;
    const knownSaved = savedTeams.filter(isKnownTeam);
    if (knownSaved.length === 0) {
      clearSticky();
      return;
    }
    updateQuery({ team: asQueryValue(knownSaved) }, 'team_view_sticky');
  }, [teamsEnabled, teamsFetched, currentParams.team, teams, updateQuery]);

  const handleTeamViewChange = (rawValue) => {
    // Multi-select: "No teams" (unassigned) CAN combine with specific team slugs —
    // the backend ORs the untagged scope with the selected-team scope — so every
    // selected value is preserved (no mutual exclusion).
    const nextValues = normalizeTeamViewSelection(rawValue);

    // Send only the view CATEGORY (all/unassigned/team), never the team slug/name.
    captureAnalytics('referral_team_view_changed', { view: teamViewCategory(nextValues) });

    if (nextValues.length === 0) {
      try { localStorage.removeItem(REFERRAL_TEAM_VIEW_STORAGE_KEY); } catch { /* ignore */ }
      updateQuery({ team: null }, 'team_view');
      return;
    }
    try { localStorage.setItem(REFERRAL_TEAM_VIEW_STORAGE_KEY, nextValues.join(',')); } catch { /* ignore */ }
    updateQuery({ team: teamViewQueryValue(nextValues) }, 'team_view');
  };

  const teamViewOptions = useMemo(() => ([
    { value: TEAM_VIEW_UNASSIGNED, name: 'No Teams', label: 'No Teams' },
    ...(teams || []).map((team) => ({ value: team.id, name: team.name, label: teamViewChip(team) })),
  ]), [teams]);
  const selectedTeamViews = normalizeToArray(currentParams.team);

  // --- Handlers ---
  const clearInProgressRef = useRef(false);

  const handleSearch = (value) => {
    clearInProgressRef.current = !value;
    setSearchValue(value);
    updateQuery({ contains: value || null, search: null }, 'search');
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    // Trigger search when field is cleared via backspace (not via allowClear X button click,
    // which already fires onSearch → handleSearch and would cause a duplicate updateQuery)
    if (value === '' && !clearInProgressRef.current && (currentParams.contains || currentParams.search)) {
      updateQuery({ contains: null, search: null }, 'search');
    }
    clearInProgressRef.current = false;
  };

  const handleStatusChange = (values) => {
    const hasScheduledStatus = normalizeStatusValues(values).some(
      (value) => value === SCHEDULED_REFERRAL_STATUS || value === MOHS_SCHEDULED_REFERRAL_STATUS,
    );
    updateQuery({
      referral_statuses: values.length > 0 ? values : null,
      scheduled: !hasScheduledStatus ? null : currentParams.scheduled,
    }, 'status');
  };

  const handleSortChange = (value) => {
    switch (value) {
      case 'newest':
        return updateQuery({ sort_dir: 'desc', sort: null }, 'sort');
      case 'oldest':
        return updateQuery({ sort_dir: 'asc', sort: null }, 'sort');
      case 'dob_youngest':
        return updateQuery({ sort: 'dob', sort_dir: null }, 'sort');
      case 'dob_oldest':
        return updateQuery({ sort: 'dob', sort_dir: 'asc' }, 'sort');
      default:
        break;
    }
  };

  const handleArchivedChange = (value) => {
    if (value === 'active') return updateQuery({ archived: null }, 'archived');
    if (value === 'archived') return updateQuery({ archived: 'true' }, 'archived');
    return updateQuery({ archived: 'both' }, 'archived');
  };

  const handleDepartmentChange = (values) => {
    updateQuery({ department: values.length > 0 ? values : null }, 'department');
  };

  const handleLabelChange = (values) => {
    updateQuery({ tag: values.length > 0 ? values : null }, 'label');
  };

  const handleAssigneeChange = (values) => {
    updateQuery({ assignee: values.length > 0 ? values : null }, 'assignee');
  };

  const handleAssigneeDropdownVisibleChange = useCallback((open) => {
    if (!open || hasRequestedDepartmentUsers) return;
    fetchDepartmentUsers('?page_size=10000');
    setHasRequestedDepartmentUsers(true);
  }, [fetchDepartmentUsers, hasRequestedDepartmentUsers]);

  const handleDobChange = (date, dateString) => {
    if (!date) return updateQuery({ dob: null }, 'dob');
    return updateQuery({ dob: moment(dateString, dateFormat).format('YYYY-MM-DD') }, 'dob');
  };

  const handleDateRangeChange = (prefix) => (dates) => {
    const {
      startKey, endKey, source, asInstants,
    } = DATE_RANGE_CONFIGS[prefix];
    if (!dates || (!dates[0] && !dates[1])) {
      return updateQuery({ [startKey]: null, [endKey]: null }, source);
    }
    if (asInstants) {
      return updateQuery({
        [startKey]: toLocalDayStartInstant(dates[0]),
        [endKey]: toLocalDayEndInstant(dates[1]),
      }, source);
    }
    return updateQuery({
      [startKey]: dates[0] ? dates[0].format('YYYY-MM-DD') : null,
      [endKey]: dates[1] ? dates[1].format('YYYY-MM-DD') : null,
    }, source);
  };

  const handleReplyStatusChange = (values) => {
    updateQuery({ reply_statuses: values.length > 0 ? values : null }, 'reply_status');
  };

  const handleSmsStatusChange = (values) => {
    updateQuery({ sms_statuses: values.length > 0 ? values : null }, 'sms_status');
  };

  const handleOverdueToggle = (checked) => {
    updateQuery({ overdue: checked || null }, 'overdue_toggle');
  };

  const handleUnassignedToggle = (checked) => {
    updateQuery({ unassigned: checked || null }, 'unassigned_toggle');
  };

  const handleUnreadToggle = (checked) => {
    updateQuery({ unread: checked || null }, 'unread_toggle');
  };

  const isAwaitingProviderActive = awaitingProviderEnabled
    && (currentParams[AWAITING_PROVIDER_FILTER_KEY] === 'true' || currentParams[AWAITING_PROVIDER_FILTER_KEY] === true);
  const handleAwaitingProviderToggle = () => updateQuery(
    { [AWAITING_PROVIDER_FILTER_KEY]: isAwaitingProviderActive ? null : 'true' },
    'awaiting_provider_quick_filter',
  );

  // Auto upload filter handlers
  const selectedAutoUploadActions = useMemo(() => (
    AUTO_UPLOAD_KEYS.filter((key) => currentParams[key] === 'true' || currentParams[key] === true)
  ), [currentParams]);

  const autoUploadOptions = useAutoUploadFilterOptions({
    createPatientChart,
    taskCreation,
    hideTaskButton,
    selectedValues: selectedAutoUploadActions,
    enabled: autoUploadToEMR,
  });

  const handleAutoUploadChange = (values) => {
    const normalizedValues = normalizeAutoUploadFilterValues(values, selectedAutoUploadActions);
    const updates = AUTO_UPLOAD_KEYS.reduce((acc, key) => {
      acc[key] = normalizedValues.includes(key) ? 'true' : null;
      return acc;
    }, {});
    updateQuery(updates, 'auto_upload_action');
  };

  const isFailedUploadsActive = currentParams.failedAutoUploaded === 'true' || currentParams.failedAutoUploaded === true;

  const handleFailedUploadsQuickFilter = useCallback(() => {
    const updates = { failedAutoUploaded: isFailedUploadsActive ? null : 'true' };
    if (!isFailedUploadsActive) updates.notAttemptedAutoUploaded = null;
    updateQuery(updates, 'quick_filter_failed_uploads');
  }, [isFailedUploadsActive, updateQuery]);

  const handleClearAll = () => {
    setSearchValue('');
    if (savedViewsEnabled) {
      // Flag on: the shared null-list (every filter, sort, team, legacy constraint
      // and the selected view), page 1, and the page's sticky team removed.
      didApplyTeamStickyRef.current = true;
      try { localStorage.removeItem(REFERRAL_TEAM_VIEW_STORAGE_KEY); } catch { /* ignore */ }
      updateQuery(buildSavedViewQueryUpdates({ page: SAVED_VIEWS_PAGE }), 'clear_all');
      return;
    }
    const clearFilters = TRACKABLE_FILTER_KEYS.reduce((acc, key) => {
      acc[key] = null;
      return acc;
    }, { search: null });
    updateQuery(clearFilters, 'clear_all');
  };

  // --- Saved views (ENG-3551; inert unless savedViewsEnabled) ---
  const trackSavedViewInteraction = useCallback((action, extra = {}) => {
    captureAnalytics(SAVED_VIEW_ANALYTICS_EVENT, { page: SAVED_VIEWS_PAGE, action, ...extra });
  }, []);

  // The bar's Clear all is one deliberate interaction whatever it clears (a team-only
  // or selection-only clear has no per-key filter event).
  const handleClearAllClick = () => {
    if (savedViewsEnabled) trackSavedViewInteraction('clear_filters');
    handleClearAll();
  };

  // Names the page already knows, so a reference deleted elsewhere is reported by
  // its last known name rather than its slug or email.
  const savedViewKnownLabels = useMemo(() => (savedViewsEnabled ? {
    tag: Object.fromEntries((labels || []).map((label) => [String(label.id ?? label.name), label.name])),
    team: Object.fromEntries((teams || []).map((team) => [String(team.id), team.name])),
    assignee: Object.fromEntries((departmentUsers || []).map((user) => [String(user[0]), user[1] || user[0]])),
  } : {}), [savedViewsEnabled, labels, teams, departmentUsers]);

  const savedViewOptions = useSavedViewOptions({
    page: SAVED_VIEWS_PAGE,
    enabled: savedViewsEnabled,
    orgId: savedViewsOrgId,
    departments,
    knownLabels: savedViewKnownLabels,
  });

  const applySavedViewUpdates = useCallback((updates) => {
    didApplyTeamStickyRef.current = true;
    updateQuery(updates, 'saved_view');
  }, [updateQuery]);

  // Selection-only change (create, delete, invalid marker): history.replace of the
  // live URL with only the marker changed, no row fetch.
  const replaceSavedViewMarker = useCallback((viewId) => {
    didApplyTeamStickyRef.current = true;
    const nextParams = { ...liveParamsRef.current };
    if (viewId === null || viewId === undefined) delete nextParams[SAVED_VIEW_MARKER_KEY];
    else nextParams[SAVED_VIEW_MARKER_KEY] = String(viewId);
    liveParamsRef.current = nextParams;
    const { stringify, stringifyOptions } = paramTools;
    const writeLocation = paramTools.replace || paramTools.push;
    writeLocation(`${window.location.pathname}${stringify(nextParams, stringifyOptions)}`);
  }, [paramTools]);

  const savedViews = useSavedViews({
    page: SAVED_VIEWS_PAGE,
    enabled: savedViewsEnabled,
    orgId: savedViewsOrgId,
    userId: savedViewsUserId,
    params: currentParams,
    updateQuery: applySavedViewUpdates,
    replaceMarker: replaceSavedViewMarker,
    options: savedViewOptions,
    storageKey: REFERRAL_TEAM_VIEW_STORAGE_KEY,
    list: savedViewsList,
    fetchSavedViews: dispatchFetchSavedViews,
    createSavedView: dispatchCreateSavedView,
    updateSavedView: dispatchUpdateSavedView,
    deleteSavedView: dispatchDeleteSavedView,
    onInteraction: trackSavedViewInteraction,
  });

  // Publish the bar's own Clear all for the records HOC's empty state, so the
  // empty-state button never builds a second clear payload.
  const handleClearAllRef = useRef(handleClearAll);
  useLayoutEffect(() => {
    handleClearAllRef.current = handleClearAll;
  });
  useEffect(() => {
    if (!savedViewsEnabled || !savedViewsControlsRef) return undefined;
    // eslint-disable-next-line no-param-reassign
    savedViewsControlsRef.current = {
      clear: ({ source } = {}) => {
        if (source === 'empty_state') trackSavedViewInteraction('clear_filters');
        handleClearAllRef.current();
      },
    };
    return () => {
      // eslint-disable-next-line no-param-reassign
      savedViewsControlsRef.current = null;
    };
  }, [savedViewsEnabled, savedViewsControlsRef, trackSavedViewInteraction]);

  // With saved views on, a table label chip is this bar's own filter gesture, so it
  // keeps the selected view's marker and goes through the HOC's request wrapper.
  // Off, the page wrapper keeps its original push + direct fetch.
  useEffect(() => {
    if (!savedViewsEnabled || !labelFilterRef) return undefined;
    // eslint-disable-next-line no-param-reassign
    labelFilterRef.current = (labelId) => {
      if (labelId == null || String(labelId) === '') return;
      const selectedLabels = normalizeToArray(liveParamsRef.current.tag).map(String);
      if (selectedLabels.includes(String(labelId))) return;
      updateQuery({ tag: [...selectedLabels, String(labelId)] }, 'label_chip');
    };
    return () => {
      // eslint-disable-next-line no-param-reassign
      labelFilterRef.current = null;
    };
  }, [savedViewsEnabled, labelFilterRef, updateQuery]);

  const savedViewsActive = savedViewsEnabled && savedViews.available;
  const [savedViewFocusRequest, setSavedViewFocusRequest] = useState(0);
  const showClearAll = savedViewsEnabled
    ? canClearSavedViewState(currentParams, SAVED_VIEWS_PAGE)
    : activeFilterCount > 0;
  // Save view needs something a view would keep: a saveable filter or a
  // non-default sort. Search, DOB and dates alone would save an empty view. An
  // invalid URL value still shows it, so the save explains what is wrong.
  const hasSaveableState = useMemo(() => {
    if (!savedViewsEnabled) return false;
    const { filters, error } = canonicalizeFilters(pickSaveableParams(currentParams, SAVED_VIEWS_PAGE), SAVED_VIEWS_PAGE);
    return Boolean(error) || Object.keys(filters).length > 0;
  }, [savedViewsEnabled, currentParams]);
  const showSaveView = savedViewsActive && !savedViews.selectedView && hasSaveableState;

  const savedViewSummary = useMemo(() => {
    if (!savedViewsEnabled || !savedViews.selectedView) return '';
    return formatSavedViewSummary(currentParams, SAVED_VIEWS_PAGE, {
      tag: readyOptions((labels || []).map((label) => ({ value: String(label.id ?? label.name), label: label.name }))),
      team: readyOptions((teams || []).map((team) => ({ value: String(team.id), label: team.name }))),
      department: readyOptions((departments || []).map((department) => ({ value: String(department.slug), label: department.name }))),
      referral_statuses: readyOptions((Array.isArray(allReferredStatuses) ? allReferredStatuses : [])
        .map((status) => ({ value: String(status.name), label: status.name }))),
      assignee: readyOptions((departmentUsers || []).map((user) => ({ value: String(user[0]), label: String(user[1] || user[0]) }))),
      reply_statuses: readyOptions(REPLY_STATUS_OPTIONS),
      sms_statuses: readyOptions(SMS_STATUS_OPTIONS),
    });
  }, [savedViewsEnabled, savedViews.selectedView, currentParams, labels, teams, departments, allReferredStatuses, departmentUsers]);

  // --- Derived values ---
  const sortValue = useMemo(() => {
    if (currentParams.sort === 'dob' && currentParams.sort_dir === 'asc') return 'dob_oldest';
    if (currentParams.sort === 'dob') return 'dob_youngest';
    if (currentParams.sort_dir === 'asc') return 'oldest';
    return 'newest';
  }, [currentParams.sort, currentParams.sort_dir]);

  const archivedValue = useMemo(() => {
    if (currentParams.archived === 'true') return 'archived';
    if (currentParams.archived === 'both') return 'both';
    return 'active';
  }, [currentParams.archived]);

  const statusOptions = useMemo(() => {
    if (!Array.isArray(allReferredStatuses) || allReferredStatuses.length === 0) return [];
    return [...allReferredStatuses]
      .sort((a, b) => (b.active === true ? 1 : 0) - (a.active === true ? 1 : 0))
      .map((status) => ({
        ...status,
        label: `${status.name}${status.active === false ? ' (Inactive)' : ''}`,
        value: status.name,
      }));
  }, [allReferredStatuses]);

  const departmentOptions = useMemo(() => {
    if (!departments || departments.length <= 1) return [];
    return sortByNameAlphabetically(departments)
      .map((d) => ({ label: d.name, value: d.slug }));
  }, [departments]);

  const labelOptions = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((l) => ({ label: l.name, value: l.id?.toString() || l.name }));
  }, [labels]);

  const assigneeOptions = useMemo(() => {
    if (!departmentUsers || departmentUsers.length === 0) return [];
    return departmentUsers.map((u) => ({ label: u[1], value: u[0] }));
  }, [departmentUsers]);

  const selectedStatuses = normalizeToArray(currentParams.referral_statuses);
  const selectedDepartments = normalizeToArray(currentParams.department);
  const selectedLabels = normalizeToArray(currentParams.tag);
  const selectedAssignees = normalizeToArray(currentParams.assignee);
  const selectedReplyStatuses = normalizeToArray(currentParams.reply_statuses);
  const selectedSmsStatuses = normalizeToArray(currentParams.sms_statuses);
  const normalizedCurrentUserEmail = (currentUserEmail || '').trim().toLowerCase();
  const normalizedCurrentUserName = (currentUserName || '').trim().toLowerCase();
  const newStatusValue = useMemo(
    () => statusOptions.find((option) => String(option.name).toLowerCase() === NEW_REFERRAL_STATUS)?.name || null,
    [statusOptions],
  );
  const myAssigneeValue = useMemo(() => {
    if (assigneeOptions.length === 0) return null;

    const meMatches = (option) => {
      const normalizedOptionLabel = String(option.label || '').trim().toLowerCase();
      const normalizedOptionValue = String(option.value || '').trim().toLowerCase();
      return (
        (normalizedCurrentUserEmail
          && (normalizedOptionLabel === normalizedCurrentUserEmail || normalizedOptionValue === normalizedCurrentUserEmail))
        || (normalizedCurrentUserName
          && (normalizedOptionLabel === normalizedCurrentUserName || normalizedOptionValue === normalizedCurrentUserName))
      );
    };

    const meOption = assigneeOptions.find(meMatches);
    if (meOption) return meOption.value;

    // Fallback to email when department users options haven't loaded yet.
    return currentUserEmail || null;
  }, [assigneeOptions, currentUserEmail, normalizedCurrentUserEmail, normalizedCurrentUserName]);

  const scheduledQuickFilterStatusValues = useMemo(
    () => statusOptions
      .filter((option) => {
        const normalizedValue = String(option.value).toLowerCase();
        return normalizedValue === SCHEDULED_REFERRAL_STATUS || normalizedValue === MOHS_SCHEDULED_REFERRAL_STATUS;
      })
      .map((option) => option.value),
    [statusOptions],
  );
  const hasScheduledStatusOption = scheduledQuickFilterStatusValues.length > 0;

  const hasNewStatusOption = !!newStatusValue;
  const hasAssignedToMeQuickFilter = !!(myAssigneeValue || currentUserEmail);
  const isNewQuickFilterActive = useMemo(
    () => selectedStatuses.length === 1 && String(selectedStatuses[0]).toLowerCase() === NEW_REFERRAL_STATUS,
    [selectedStatuses],
  );
  const isScheduledQuickFilterActive = useMemo(() => (
    currentParams.scheduled === true || currentParams.scheduled === 'true'
  ), [currentParams.scheduled]);
  const shouldClearScheduledQuickFilterStatuses = useMemo(
    () => areStatusSetsEqual(selectedStatuses, scheduledQuickFilterStatusValues),
    [selectedStatuses, scheduledQuickFilterStatusValues],
  );
  const isAssignedToMeQuickFilterActive = useMemo(() => {
    if (selectedAssignees.length === 0) return false;
    const target = String(myAssigneeValue || currentUserEmail || '').toLowerCase();
    if (!target) return false;
    return selectedAssignees.some((assignee) => String(assignee).toLowerCase() === target);
  }, [selectedAssignees, myAssigneeValue, currentUserEmail]);
  const isOverdueQuickFilterActive = !!currentParams.overdue;
  const isUnreadQuickFilterActive = !!currentParams.unread;

  const handleNewQuickFilter = useCallback(() => {
    if (isNewQuickFilterActive) {
      updateQuery({ referral_statuses: null }, 'quick_filter_new');
      return;
    }

    if (!newStatusValue) return;
    updateQuery({ referral_statuses: [newStatusValue] }, 'quick_filter_new');
  }, [isNewQuickFilterActive, newStatusValue, updateQuery]);

  const handleScheduledQuickFilter = useCallback(() => {
    if (isScheduledQuickFilterActive) {
      const updates = { scheduled: null };
      if (shouldClearScheduledQuickFilterStatuses) updates.referral_statuses = null;
      updateQuery(updates, 'quick_filter_scheduled');
      return;
    }

    if (scheduledQuickFilterStatusValues.length === 0) return;
    updateQuery({ referral_statuses: scheduledQuickFilterStatusValues, scheduled: true }, 'quick_filter_scheduled');
  }, [
    isScheduledQuickFilterActive,
    scheduledQuickFilterStatusValues,
    shouldClearScheduledQuickFilterStatuses,
    updateQuery,
  ]);

  const handleAssignedToMeQuickFilter = useCallback(() => {
    const targetAssignee = myAssigneeValue || currentUserEmail;
    if (!targetAssignee) return;

    const normalizedTarget = String(targetAssignee).toLowerCase();
    const hasTargetAssignee = selectedAssignees.some(
      (assignee) => String(assignee).toLowerCase() === normalizedTarget,
    );

    if (hasTargetAssignee) {
      const nextAssignees = selectedAssignees.filter(
        (assignee) => String(assignee).toLowerCase() !== normalizedTarget,
      );
      updateQuery({ assignee: nextAssignees.length > 0 ? nextAssignees : null }, 'quick_filter_assigned_to_me');
      return;
    }

    updateQuery({ assignee: [...selectedAssignees, targetAssignee] }, 'quick_filter_assigned_to_me');
  }, [myAssigneeValue, currentUserEmail, selectedAssignees, updateQuery]);

  const handleOverdueQuickFilter = useCallback(() => {
    updateQuery({ overdue: isOverdueQuickFilterActive ? null : true }, 'quick_filter_overdue');
  }, [isOverdueQuickFilterActive, updateQuery]);

  return (
    <div className="referral-filter-bar">
      {savedViewsEnabled && (savedViewsActive || savedViews.notice) && (
        <SavedViewsBar
          views={savedViewsActive ? savedViews.views : []}
          selectedView={savedViewsActive ? savedViews.selectedView : null}
          isModified={savedViewsActive && savedViews.isModified}
          saving={savedViews.saving}
          notice={savedViews.notice}
          summary={savedViewSummary}
          onApply={savedViews.apply}
          onUpdate={savedViews.update}
          onSaveAsNew={savedViews.saveNew}
          onRevert={savedViews.revert}
          onRename={savedViews.rename}
          onDelete={savedViews.remove}
          onRetry={savedViews.retry}
          onDismissNotice={savedViews.dismissNotice}
          onInteraction={trackSavedViewInteraction}
          focusRequest={savedViewFocusRequest}
        />
      )}
      {/* Primary filters row */}
      <div className="referral-filter-bar__row">
        <Input.Search
          className="referral-filter-bar__search"
          placeholder="Search Patient Name..."
          allowClear
          value={searchValue}
          onChange={handleSearchChange}
          onSearch={handleSearch}
        />

        <DatePicker
          className="referral-filter-bar__date"
          placeholder="DOB"
          format={dateFormat}
          value={currentParams.dob ? moment(currentParams.dob, 'YYYY-MM-DD') : null}
          onChange={handleDobChange}
        />

        {statusOptions.length > 0 && (
          <Select
            className="referral-filter-bar__select referral-filter-bar__select--status compact-multi-select"
            mode="multiple"
            placeholder="Status"
            value={selectedStatuses}
            onChange={handleStatusChange}
            allowClear
            onClear={() => handleStatusChange([])}
            showSearch
            optionFilterProp="data-search-text"
            filterOption={(input, option) => (
              String(option?.['data-search-text'] || '').toLowerCase().includes(input.toLowerCase())
            )}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedStatuses)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('statuses')}
          >
            {statusOptions.map((status, index) => (
              <Select.Option
                key={status.id}
                value={status.name}
                disabled={status.active === false}
                data-search-text={status.name}
              >
                <ReferralStatusLabel
                  status={status}
                  statuses={statusOptions}
                  fallbackIndex={index}
                  inactive={status.active === false}
                />
              </Select.Option>
            ))}
          </Select>
        )}

        {hasNewStatusOption && (
          <button
            type="button"
            className={`referral-filter-bar__quick-filter${isNewQuickFilterActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
            onClick={handleNewQuickFilter}
            aria-pressed={isNewQuickFilterActive}
          >
            <span>{isNewQuickFilterActive ? 'Showing New Only' : 'Show New Only'}</span>
          </button>
        )}

        {hasScheduledStatusOption && (
          <button
            type="button"
            className={`referral-filter-bar__quick-filter${isScheduledQuickFilterActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
            onClick={handleScheduledQuickFilter}
            aria-pressed={isScheduledQuickFilterActive}
          >
            <span>Scheduled</span>
          </button>
        )}

        {hasAssignedToMeQuickFilter && (
          <button
            type="button"
            className={`referral-filter-bar__quick-filter${isAssignedToMeQuickFilterActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
            onClick={handleAssignedToMeQuickFilter}
            aria-pressed={isAssignedToMeQuickFilterActive}
          >
            <span>Assigned to Me</span>
          </button>
        )}

        <button
          type="button"
          className={`referral-filter-bar__quick-filter${isOverdueQuickFilterActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
          onClick={handleOverdueQuickFilter}
          aria-pressed={isOverdueQuickFilterActive}
        >
          <span>Overdue</span>
        </button>

        <button
          type="button"
          className={`referral-filter-bar__quick-filter${isUnreadQuickFilterActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
          onClick={() => handleUnreadToggle(!isUnreadQuickFilterActive)}
          aria-pressed={isUnreadQuickFilterActive}
        >
          <span>Unread</span>
        </button>

        {awaitingProviderEnabled && (
          <button
            type="button"
            className={`referral-filter-bar__quick-filter${isAwaitingProviderActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
            onClick={handleAwaitingProviderToggle}
            aria-pressed={isAwaitingProviderActive}
            title="Referrals set aside until their referring provider is added to the EHR"
          >
            <span>{AWAITING_PROVIDER_LABEL}</span>
          </button>
        )}

        <Select
          className="referral-filter-bar__select referral-filter-bar__select--fixed"
          value={sortValue}
          onChange={handleSortChange}
          options={SORT_OPTIONS}
        />

        <Select
          className="referral-filter-bar__select referral-filter-bar__select--fixed"
          value={archivedValue}
          onChange={handleArchivedChange}
          options={ARCHIVED_OPTIONS}
        />

        {teamsEnabled && (
          <Select
            className="referral-filter-bar__select compact-multi-select"
            mode="multiple"
            placeholder="All Teams"
            value={selectedTeamViews}
            onChange={handleTeamViewChange}
            options={teamViewOptions}
            optionLabelProp="label"
            allowClear
            onClear={() => handleTeamViewChange([])}
            showSearch
            filterOption={(input, option) => (
              String(option?.name || '').toLowerCase().includes(input.toLowerCase())
            )}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedTeamViews)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('teams')}
          />
        )}

        {departmentOptions.length > 0 && (
          <Select
            className="referral-filter-bar__select referral-filter-bar__select--dept compact-multi-select"
            mode="multiple"
            placeholder="Department"
            value={selectedDepartments}
            onChange={handleDepartmentChange}
            options={departmentOptions}
            dropdownMatchSelectWidth={false}
            dropdownClassName="department-filter-dropdown"
            dropdownAlign={{ overflow: { adjustX: 1, adjustY: 1 } }}
            allowClear
            onClear={() => handleDepartmentChange([])}
            showSearch
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedDepartments)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('departments')}
          />
        )}

        <button
          type="button"
          className={`referral-filter-bar__more-btn${showMoreFilters ? ' referral-filter-bar__more-btn--active' : ''}`}
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          <FunnelSimple size={15} weight={showMoreFilters ? 'fill' : 'regular'} />
          <span>Filters</span>
          {showMoreFilters ? <CaretUp size={11} /> : <CaretDown size={11} />}
        </button>

        {showClearAll && (
          <button
            type="button"
            className="referral-filter-bar__clear-btn"
            onClick={handleClearAllClick}
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        )}

        {showSaveView && (
          <SaveViewButton
            saving={savedViews.saving}
            onOpen={() => trackSavedViewInteraction('open_save')}
            onCancel={() => trackSavedViewInteraction('cancel_save')}
            onSave={async (name) => {
              trackSavedViewInteraction('save');
              const result = await savedViews.saveNew(name);
              if (result?.ok) setSavedViewFocusRequest((count) => count + 1);
              return result;
            }}
          />
        )}
      </div>

      {/* Secondary filters - expandable */}
      {showMoreFilters && (
        <div className="referral-filter-bar__row referral-filter-bar__row--secondary">
          {autoUploadToEMR && (
            <button
              type="button"
              className={`referral-filter-bar__quick-filter${isFailedUploadsActive ? ' referral-filter-bar__quick-filter--active' : ''}`}
              onClick={handleFailedUploadsQuickFilter}
              aria-pressed={isFailedUploadsActive}
            >
              <span>Failed Uploads</span>
            </button>
          )}

          <Select
            className="referral-filter-bar__select compact-multi-select"
            mode="multiple"
            placeholder="Label"
            value={selectedLabels}
            onChange={handleLabelChange}
            options={labelOptions}
            allowClear
            onClear={() => handleLabelChange([])}
            showSearch
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedLabels)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('labels')}
          />

          <Select
            className="referral-filter-bar__select compact-multi-select"
            mode="multiple"
            placeholder="Assignee"
            value={selectedAssignees}
            onChange={handleAssigneeChange}
            options={assigneeOptions}
            allowClear
            onClear={() => handleAssigneeChange([])}
            showSearch
            onDropdownVisibleChange={handleAssigneeDropdownVisibleChange}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedAssignees)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('assignees')}
          />

          <Tooltip title="Date the referral was received" trigger={['hover', 'focus']}>
            <DatePicker.RangePicker
              className="referral-filter-bar__date-range"
              placeholder={['Referred from', 'Referred to']}
              format={dateFormat}
              allowEmpty={[true, true]}
              value={[
                currentParams.date_start ? parseDateFilterParam(currentParams.date_start) : null,
                currentParams.date_end ? parseDateFilterParam(currentParams.date_end) : null,
              ]}
              onChange={handleDateRangeChange('received')}
            />
          </Tooltip>

          <Tooltip title="Due date for the referral" trigger={['hover', 'focus']}>
            <DatePicker.RangePicker
              className="referral-filter-bar__date-range"
              placeholder={['Due from', 'Due to']}
              format={dateFormat}
              allowEmpty={[true, true]}
              value={[
                currentParams.due_date_start ? moment(currentParams.due_date_start, 'YYYY-MM-DD') : null,
                currentParams.due_date_end ? moment(currentParams.due_date_end, 'YYYY-MM-DD') : null,
              ]}
              onChange={handleDateRangeChange('due')}
            />
          </Tooltip>

          <Tooltip title="Scheduled appointment date" trigger={['hover', 'focus']}>
            <DatePicker.RangePicker
              className="referral-filter-bar__date-range"
              placeholder={['Appt from', 'Appt to']}
              format={dateFormat}
              allowEmpty={[true, true]}
              value={[
                currentParams.appointment_date_start ? moment(currentParams.appointment_date_start, 'YYYY-MM-DD') : null,
                currentParams.appointment_date_end ? moment(currentParams.appointment_date_end, 'YYYY-MM-DD') : null,
              ]}
              onChange={handleDateRangeChange('appointment')}
            />
          </Tooltip>

          <Select
            className="referral-filter-bar__select referral-filter-bar__select--small compact-multi-select"
            mode="multiple"
            placeholder="Reply Status"
            value={selectedReplyStatuses}
            onChange={handleReplyStatusChange}
            options={REPLY_STATUS_OPTIONS}
            allowClear
            onClear={() => handleReplyStatusChange([])}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedReplyStatuses)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('reply statuses')}
          />

          <Select
            className="referral-filter-bar__select referral-filter-bar__select--small compact-multi-select"
            mode="multiple"
            placeholder="SMS Status"
            value={selectedSmsStatuses}
            onChange={handleSmsStatusChange}
            options={SMS_STATUS_OPTIONS}
            allowClear
            onClear={() => handleSmsStatusChange([])}
            maxTagCount={getCompactMultiSelectMaxTagCount(selectedSmsStatuses)}
            maxTagPlaceholder={getCompactMultiSelectPlaceholder('SMS statuses')}
          />

          {autoUploadToEMR && (
            <Select
              className="referral-filter-bar__select compact-multi-select"
              mode="multiple"
              placeholder="Auto Upload Status"
              value={selectedAutoUploadActions}
              onChange={handleAutoUploadChange}
              options={autoUploadOptions}
              allowClear
              onClear={() => handleAutoUploadChange([])}
              maxTagCount={getCompactMultiSelectMaxTagCount(selectedAutoUploadActions)}
              maxTagPlaceholder={getCompactMultiSelectPlaceholder('auto upload statuses')}
            />
          )}

          <div className="referral-filter-bar__toggles">
            <Tag.CheckableTag
              className="referral-filter-bar__toggle"
              checked={!!currentParams.overdue}
              onChange={handleOverdueToggle}
            >
              Overdue
            </Tag.CheckableTag>
            <Tag.CheckableTag
              className="referral-filter-bar__toggle"
              checked={!!currentParams.unassigned}
              onChange={handleUnassignedToggle}
            >
              Unassigned
            </Tag.CheckableTag>
          </div>
        </div>
      )}
    </div>
  );
};

ReferralFilterBar.defaultProps = {
  allReferredStatuses: [],
  departments: [],
  labels: [],
  departmentUsers: [],
  currentUserEmail: '',
  currentUserName: '',
  teams: [],
  autoUploadToEMR: false,
  createPatientChart: false,
  taskCreation: false,
  hideTaskButton: false,
  labelFilterRef: null,
  savedViewsEnabled: false,
  savedViewsControlsRef: null,
  savedViewsList: undefined,
  savedViewsUserId: null,
  savedViewsOrgId: null,
  fetchSavedViews: undefined,
  createSavedView: undefined,
  updateSavedView: undefined,
  deleteSavedView: undefined,
};

ReferralFilterBar.propTypes = {
  paramTools: PropTypes.object.isRequired,
  getPageDataWithQuery: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  allReferredStatuses: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  departments: PropTypes.array,
  labels: PropTypes.array,
  departmentUsers: PropTypes.array,
  currentUserEmail: PropTypes.string,
  currentUserName: PropTypes.string,
  teams: PropTypes.array,
  getLabelsByQuery: PropTypes.func.isRequired,
  getUserDepartmentsByQuery: PropTypes.func.isRequired,
  getOrganizationTeams: PropTypes.func.isRequired,
  autoUploadToEMR: PropTypes.bool,
  createPatientChart: PropTypes.bool,
  taskCreation: PropTypes.bool,
  hideTaskButton: PropTypes.bool,
  labelFilterRef: PropTypes.object,
  savedViewsEnabled: PropTypes.bool,
  savedViewsControlsRef: PropTypes.object,
  savedViewsList: PropTypes.object,
  savedViewsUserId: PropTypes.string,
  savedViewsOrgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchSavedViews: PropTypes.func,
  createSavedView: PropTypes.func,
  updateSavedView: PropTypes.func,
  deleteSavedView: PropTypes.func,
};

export default connect(
  (state) => {
    const savedViewsUserId = state.auth?.data?.uid ?? null;
    const savedViewsOrgId = state.auth?.currentOrganizationId ?? null;
    return {
      allReferredStatuses: state.userProfile.allReferredStatuses,
      departments: state.auth.departments,
      labels: state.labels.data,
      departmentUsers: state.userProfile.departmentUsers,
      currentUserEmail: state.auth?.data?.uid || '',
      currentUserName: state.auth?.data?.fullName || '',
      teams: state.teams.data,
      savedViewsUserId,
      savedViewsOrgId,
      savedViewsList: selectSavedViewsList(state, { userId: savedViewsUserId, orgId: savedViewsOrgId, page: SAVED_VIEWS_PAGE }),
    };
  },
  {
    getLabelsByQuery,
    getUserDepartmentsByQuery,
    getOrganizationTeams,
    fetchSavedViews,
    createSavedView,
    updateSavedView,
    deleteSavedView,
  },
)(ReferralFilterBar);
